import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EditDocumentPage.css';

const API_BASE_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  // Empty URL means same-origin (production on Cloud Run)
  return url ? url + '/api' : '/api';
})();

const EditDocumentPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    setLoading(false);
  }, [documentId]);

  const handleDownloadForEdit = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication required. Please login again.');
        return;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/export/docx`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `legal_opinion_${documentId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccessMessage('✅ Document downloaded! Edit it in MS Word, then upload the edited version below.');
    } catch (err) {
      console.error('Error downloading document:', err);
      if (err.response?.status === 401) {
        alert('Authentication failed. Please try logging out and logging in again, or use a regular browser window (not incognito).');
      } else {
        alert('Failed to download document. Please try again or use a regular browser window.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.docx')) {
      setSelectedFile(file);
      setSuccessMessage('');
    } else {
      alert('Please select a valid DOCX file');
    }
  };

  const handleUploadEdited = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/upload-edited`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );
      
      if (response.data.success) {
        setSuccessMessage('✅ Document updated successfully! Your edited version has been saved.');
        setSelectedFile(null);
        document.getElementById('fileInput').value = '';
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-document-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-document-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-document-page">
      {/* Header */}
      <div className="document-header">
        <div className="header-left">
          <button onClick={() => navigate('/dashboard')} className="btn-back">
            ← Back to Dashboard
          </button>
          <h1>📝 Edit Legal Opinion Document</h1>
        </div>
      </div>

      {successMessage && (
        <div className="success-banner">
          {successMessage}
        </div>
      )}

      {/* Main Content */}
      <div className="edit-workflow-container">
        <div className="workflow-card">
          <div className="workflow-header">
            <h2>📄 Edit Document in MS Word</h2>
            <p>Edit your legal opinion in Microsoft Word with 100% format preservation</p>
          </div>

          {/* Step 1: Download */}
          <div className="workflow-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Download Document</h3>
              <p>Download the document to edit in Microsoft Word on your computer</p>
              <button 
                onClick={handleDownloadForEdit} 
                disabled={downloading} 
                className="btn-download-large"
              >
                {downloading ? '⏳ Downloading...' : '📥 Download DOCX for Editing'}
              </button>
            </div>
          </div>

          {/* Step 2: Edit */}
          <div className="workflow-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Edit in MS Word</h3>
              <p>Open the downloaded file in Microsoft Word and make your changes:</p>
              <ul className="features-list">
                <li>✓ Edit any text, tables, or formatting</li>
                <li>✓ 100% format preservation - all tables, borders, spacing maintained</li>
                <li>✓ Use all MS Word features - track changes, comments, etc.</li>
                <li>✓ Save the file when done</li>
              </ul>
            </div>
          </div>

          {/* Step 3: Upload */}
          <div className="workflow-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Upload Edited Document</h3>
              <p>Upload your edited DOCX file to replace the current version</p>
              
              <div className="upload-section">
                <input
                  id="fileInput"
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                
                {selectedFile && (
                  <div className="selected-file">
                    <span>📎 {selectedFile.name}</span>
                    <button onClick={() => {
                      setSelectedFile(null);
                      document.getElementById('fileInput').value = '';
                    }} className="btn-clear">
                      ✕
                    </button>
                  </div>
                )}
                
                <button 
                  onClick={handleUploadEdited} 
                  disabled={!selectedFile || uploading}
                  className="btn-upload-large"
                >
                  {uploading ? '⏳ Uploading...' : '⬆️ Upload Edited Document'}
                </button>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <strong>💡 Why this approach?</strong>
            <p>Editing in MS Word ensures:</p>
            <ul>
              <li>✓ 100% format preservation - no conversion issues</li>
              <li>✓ All tables, borders, and spacing remain perfect</li>
              <li>✓ No missing data or content</li>
              <li>✓ Full MS Word features available</li>
              <li>✓ Professional, reliable editing experience</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDocumentPage;
